import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  site as siteData,
  navIds,
  heroBadgeIds,
  aboutParagraphIds,
  objectiveIds,
  advantageList,
  serviceList,
  fleetStatList,
  fleetLocationList,
  galleryList,
  coverageData,
  clientList,
  contactData,
} from "../data/content";

// Builds the page content in the visitor's language: text comes from the
// locale files, everything else (icons, images, numbers, addresses) from
// data/content.js, joined by id. The result has the same shape the components
// always used, so they only swap an `import` for this hook.
export function useContent() {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => ({
      site: { ...siteData, motto: t("site.motto"), city: t("site.city") },

      nav: navIds.map((id) => ({ id, label: t(`nav.${id}`) })),

      hero: {
        eyebrow: t("hero.eyebrow"),
        headline: t("hero.headline"),
        subheadline: t("hero.subheadline"),
        ctaPrimary: t("hero.ctaPrimary"),
        ctaSecondary: t("hero.ctaSecondary"),
        badges: heroBadgeIds.map((id) => ({ id, text: t(`hero.badges.${id}`) })),
        scrollHint: t("hero.scrollHint"),
      },

      about: {
        eyebrow: t("about.eyebrow"),
        title: t("about.title"),
        paragraphs: aboutParagraphIds.map((id) => t(`about.paragraphs.${id}`)),
        mission: { label: t("about.mission.label"), text: t("about.mission.text") },
        vision: { label: t("about.vision.label"), text: t("about.vision.text") },
      },

      objectives: objectiveIds.map((id) => ({
        id,
        title: t(`objectives.${id}.title`),
        text: t(`objectives.${id}.text`),
      })),

      advantages: {
        eyebrow: t("advantages.eyebrow"),
        title: t("advantages.title"),
        items: advantageList.map(({ id, icon }) => ({
          id,
          icon,
          title: t(`advantages.items.${id}.title`),
          text: t(`advantages.items.${id}.text`),
        })),
      },

      services: {
        eyebrow: t("services.eyebrow"),
        title: t("services.title"),
        cta: t("services.cta"),
        items: serviceList.map(({ id, icon }) => ({
          id,
          icon,
          title: t(`services.items.${id}.title`),
          text: t(`services.items.${id}.text`),
        })),
      },

      fleet: {
        eyebrow: t("fleet.eyebrow"),
        title: t("fleet.title"),
        lead: t("fleet.lead"),
        whereWeAre: t("fleet.whereWeAre"),
        stats: fleetStatList.map(({ id, value, suffix }) => ({
          id,
          value,
          suffix,
          label: t(`fleet.stats.${id}`),
        })),
        locations: fleetLocationList.map(({ id, icon, address }) => ({
          id,
          icon,
          address,
          title: t(`fleet.locations.${id}.title`),
        })),
      },

      gallery: galleryList.map(({ id, src, big }) => ({
        id,
        src,
        big,
        alt: t(`gallery.${id}.alt`),
        caption: t(`gallery.${id}.caption`),
      })),

      coverage: {
        eyebrow: t("coverage.eyebrow"),
        title: t("coverage.title"),
        lead: t("coverage.lead"),
        portsLabel: t("coverage.portsLabel"),
        expansion: t("coverage.expansion"),
        mapCaption: t("coverage.mapCaption"),
        soonShort: t("coverage.soonShort"),
        states: coverageData.states,
        ports: coverageData.ports,
      },

      clients: {
        eyebrow: t("clients.eyebrow"),
        title: t("clients.title"),
        lead: t("clients.lead"),
        list: clientList,
      },

      contact: {
        eyebrow: t("contact.eyebrow"),
        title: t("contact.title"),
        lead: t("contact.lead"),
        officeTitle: t("contact.officeTitle"),
        email: contactData.email,
        office: { ...contactData.office, title: t("contact.office.title") },
        form: {
          name: t("contact.form.name"),
          company: t("contact.form.company"),
          email: t("contact.form.email"),
          phone: t("contact.form.phone"),
          message: t("contact.form.message"),
          messagePlaceholder: t("contact.form.messagePlaceholder"),
          submit: t("contact.form.submit"),
          sending: t("contact.form.sending"),
          disclaimer: t("contact.form.disclaimer"),
          success: t("contact.form.success"),
          error: t("contact.form.error"),
        },
        emailSubject: t("contact.emailSubject"),
        mailFields: {
          name: t("contact.mailFields.name"),
          company: t("contact.mailFields.company"),
          phone: t("contact.mailFields.phone"),
          email: t("contact.mailFields.email"),
          message: t("contact.mailFields.message"),
        },
      },

      footer: {
        blurb: t("footer.blurb"),
        copyright: t("footer.copyright", { year: new Date().getFullYear() }),
        closing: t("footer.closing"),
        navHeading: t("footer.navHeading"),
        pagesHeading: t("footer.pagesHeading"),
        phoneLabel: t("footer.phoneLabel"),
      },
    }),
    // `t` changes identity when the language changes, but list the language
    // explicitly so the dependency is obvious.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );
}

export default useContent;
